import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/Input";
import { Button, ButtonAdd } from "@/components/ui/button";
import { MenuItemOption } from "@/redux/services/menu/menu.type";
import useCrudMenuItemOption from "@/hooks/menu/useCrudMenuItemOption";
import { Select } from "../Select";
import MenuItemOptionValue from "./MenuItemOptionValue";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../Label";
import { TextArea } from "../Textarea";
import { cn } from "@/lib/utils";
import OptionValueIngredientsSection from "@/components/inventory/recipes/OptionValueIngredientsSection";
import { usePermissions } from "@/hooks/usePermissions";
import { useAppSelector } from "@/redux/features/hooks";
import { ChevronDown } from "lucide-react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  item?: MenuItemOption;
}

const types = [
  { value: "checkbox", label: "Case à cocher" },
  { value: "select", label: "Liste déroulante" },
  { value: "radio", label: "Bouton radio" },
];

const Field = ({ children }: { children: React.ReactNode }) => (
  <div className="[&>label]:block [&>label]:mb-1 [&>label]:text-sm [&>label]:text-card-foreground">
    {children}
  </div>
);

const Section = ({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <section>
        <CollapsibleTrigger className="w-full flex items-center justify-between py-2 border-b border-border text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-card-foreground transition-colors">
          <span>{title}</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform",
              open && "rotate-180"
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4 space-y-4">
          {children}
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
};

const MenuItemOptionDrawer: React.FC<Props> = ({ isOpen, onClose, item }) => {
  const {
    register,
    onSubmit,
    errors,
    isLoading,
    fields,
    remove,
    isTemplate,
    handleIsTemplateChange,
    hasDependency,
    handleDependencyChange,
    shouldDisplayTitle,
    handleShouldDisplayTitleChange,
    options,
    parentOption,
    handleParentOptionChange,
    appendNewValue,
    watch,
    close,
  } = useCrudMenuItemOption(onClose, item);

  const watchedValues = watch();
  const restaurant = useAppSelector((state) => state.user.currentRestaurant);
  // Recipes follow the API's inventory permissions (SFRESTO-136): the
  // section shows on inventory.view; editing needs inventory.manage, which
  // the recipe editor checks itself.
  const { can } = usePermissions();
  const canViewRecipe = can("inventory.view");
  const persistedValues: { id: string; title: string }[] = (
    watchedValues.menuItemOptionValues ?? []
  )
    .filter((v) => Boolean(v?.id && v?.title))
    .map((v) => ({ id: v.id as string, title: v.title }));

  return (
    <Sheet open={isOpen} onOpenChange={close}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col gap-0"
      >
        <form
          onSubmit={onSubmit}
          className="flex flex-col h-full min-h-0"
        >
          <header className="flex-shrink-0 px-6 py-4 border-b border-border bg-card">
            <SheetTitle className="text-base">
              {item ? "Modifier l'option" : "Ajouter une option"}
            </SheetTitle>
            <SheetDescription hidden>
              Ce panneau permet d'ajouter ou modifier une option de menu.
            </SheetDescription>
          </header>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
            <Section title="Informations">
              <Field>
                <Input
                  label="Nom"
                  type="text"
                  id="title"
                  placeholder="Nom"
                  className="h-10 py-2 text-sm"
                  {...register("title")}
                  error={errors?.title?.message}
                />
              </Field>
              <Field>
                <Select
                  label="Type"
                  id="option_type"
                  placeholder="Choisir le type"
                  required
                  options={types}
                  error={errors?.type?.message}
                  value={watchedValues.type ?? ""}
                  {...register("type")}
                />
              </Field>
              <Field>
                <Input
                  label="Position"
                  type="number"
                  id="sortOrder"
                  placeholder="Position"
                  className="h-10 py-2 text-sm"
                  {...register("sortOrder")}
                  error={errors?.sortOrder?.message}
                  min={1}
                />
              </Field>
            </Section>

            <Section title="Comportement">
              <div className="flex items-start gap-2.5">
                <Checkbox
                  id="shouldDisplayTitle"
                  className="mt-0.5"
                  {...register("shouldDisplayTitle")}
                  onCheckedChange={handleShouldDisplayTitleChange}
                  checked={shouldDisplayTitle}
                />
                <Label
                  htmlFor="shouldDisplayTitle"
                  label="Afficher le titre de l'option sur le menu"
                  id="shouldDisplayTitle"
                  className="cursor-pointer"
                />
              </div>

              <div className="flex items-start gap-2.5">
                <Checkbox
                  id="isTemplate"
                  className="mt-0.5"
                  {...register("isTemplate")}
                  onCheckedChange={handleIsTemplateChange}
                  checked={isTemplate}
                />
                <Label
                  htmlFor="isTemplate"
                  label="Cette option est un template"
                  id="isTemplate"
                  className="cursor-pointer"
                />
              </div>

              {isTemplate && (
                <div className="ml-7 border-l-2 border-border pl-3">
                  <Field>
                    <TextArea
                      label="Description du template"
                      id="templateDescription"
                      placeholder="Description du template"
                      {...register("templateDescription")}
                      error={errors?.templateDescription?.message}
                    />
                  </Field>
                </div>
              )}

              <div className="flex items-start gap-2.5">
                <Checkbox
                  id="isDependantAnotherOption"
                  className="mt-0.5"
                  {...register("isDependantAnotherOption")}
                  onCheckedChange={handleDependencyChange}
                  checked={hasDependency}
                />
                <Label
                  htmlFor="isDependantAnotherOption"
                  label="Cette option dépend d'une autre option"
                  id="isDependantAnotherOption"
                  className="cursor-pointer"
                />
              </div>

              {hasDependency && (
                <div className="ml-7 border-l-2 border-border pl-3">
                  <Field>
                    <Select
                      label="Option dépendante"
                      id="dependantOption"
                      placeholder="Choisir l'option dépendante"
                      required
                      {...register("dependsOnOption")}
                      value={watchedValues.dependsOnOption ?? ""}
                      options={options}
                      onChange={handleParentOptionChange}
                      error={errors?.dependsOnOption?.message}
                    />
                  </Field>
                </div>
              )}
            </Section>

            <Section title="Valeurs">
              {fields.length > 0 ? (
                <div className="space-y-2">
                  {fields.map((field, i) => (
                    <MenuItemOptionValue
                      key={field.id}
                      register={register}
                      errors={errors?.menuItemOptionValues?.[i]}
                      menuOptionIndex={i}
                      removeMenuItem={() => remove(i)}
                      menuItemOptionValueLength={fields.length}
                      isTemplate={isTemplate}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Aucune valeur ajoutée pour cette option
                </p>
              )}
              <ButtonAdd
                label="Ajouter une valeur"
                type="button"
                onClick={() => appendNewValue()}
              />
            </Section>

            {hasDependency && parentOption && fields.length > 0 && (
              <Section title="Modificateurs de prix">
                <div className="space-y-3">
                  {fields.map((field, valueIndex) => {
                    const fieldTitle =
                      watchedValues.menuItemOptionValues?.[valueIndex]?.title ??
                      "";
                    return (
                      <div
                        key={field.id}
                        className="border border-border rounded-md p-3 space-y-2"
                      >
                        <p className="text-sm font-medium text-card-foreground">
                          Pour "{fieldTitle}"
                        </p>
                        <div className="space-y-2">
                          {parentOption.menuItemOptionValues?.map(
                            (dependentValue, dependentValueIndex) => (
                              <div
                                key={dependentValue.id}
                                className="flex items-center gap-3"
                              >
                                <span className="flex-1 text-sm text-card-foreground truncate">
                                  {dependentValue.title}
                                </span>
                                <div className="w-24">
                                  <Input
                                    type="number"
                                    placeholder="0"
                                    variant="secondary"
                                    {...register(
                                      `menuItemOptionValues.${valueIndex}.valueDependentPrices.${dependentValueIndex}.priceModifier`
                                    )}
                                    error={
                                      errors?.menuItemOptionValues?.[
                                        valueIndex
                                      ]?.valueDependentPrices?.[
                                        dependentValueIndex
                                      ]?.priceModifier
                                    }
                                  />
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Section>
            )}

            {item && canViewRecipe && restaurant && (
              <Section title="Ingrédients par valeur">
                <OptionValueIngredientsSection
                  restaurantId={restaurant.id}
                  values={persistedValues}
                />
              </Section>
            )}
          </div>

          <footer className="flex-shrink-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={close}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "En cours..." : "Enregistrer"}
            </Button>
          </footer>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default MenuItemOptionDrawer;
